import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CustomisableVariables } from '@shared/models/customisable-variables';
import { CustomisableVariablesService } from '@shared/services/customisable-variables.service';
import { ProductHomepageService } from '@shared/services/product-homepage.service';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { ReCaptchaComponent } from '@shared/shared/components/re-captcha/re-captcha.component';
import { ISiteSettings } from '@shared/shared/interfaces/site-settings.interfaces';
import { RecaptchaService } from '@shared/shared/services/recaptcha.service';
import { Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';

@Component({
  selector: 'ten-visa-eligibility-check',
  templateUrl: './visa-eligibility-check.component.html',
  styleUrls: ['./visa-eligibility-check.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VisaEligibilityCheckComponent implements OnInit, OnDestroy {
  @Output() public closePreloginPopup: EventEmitter<boolean> = new EventEmitter<boolean>(false);
  @ViewChild(ReCaptchaComponent, { static: false })
  public captchaComponent: ReCaptchaComponent;

  public eligibilityForm: UntypedFormGroup;
  public validationErrors = this.productHomepageService.eligibilityCheckValidationErrors;
  public customVariables$: Observable<CustomisableVariables> = this.customisableVariablesService.variables;
  public phoneNumber$ = this.customisableVariablesService.getContactNumber();
  public siteSettings$: Observable<ISiteSettings> = this.siteSettingsService.fetchSiteSettings();
  public captchaLanguage: string;
  public eligibilityType: string;
  public enableRecaptcha: boolean;
  constructor(
    private productHomepageService: ProductHomepageService,
    private customisableVariablesService: CustomisableVariablesService,
    private siteSettingsService: SiteSettingsService,
    private reCaptchaService: RecaptchaService,
    private cdr: ChangeDetectorRef
  ) {
    this.setupEligibilityForm();
  }

  public ngOnInit() {
    this.reCaptchaService.setReCaptcha(true);
    this.siteSettings$
      .pipe(
        filter((siteSettings: ISiteSettings) => this.siteSettingsService.settingsRetrieved && !!siteSettings.id),
        take(1)
      )
      .subscribe((siteSettings: ISiteSettings) => {
        this.captchaLanguage = siteSettings.language;
        this.eligibilityType = siteSettings?.eligibilities_and_selfregs;
        this.enableRecaptcha =
          siteSettings?.switches?.enable_recaptchas && siteSettings?.switches?.enable_eligibility_recaptcha;

        this.cdr.detectChanges();
      });
  }

  public getCaptchaToken(captchaToken?: string) {
    this.productHomepageService.checkEligibility(
      {
        identifier: 'card_number',
        value: this.eligibilityForm.get('cardNumber').value.replace(/\s/g, ''),
        captchaToken: captchaToken,
      },
      this.eligibilityType
    );
    this.closePreloginPopup.emit(true);
  }

  /**
   * Dispatch the card number enterted by the user to be used in the relavant API call.
   * Called the user clicks onto the 'Check Eligibility and Register' button within the template
   */
  public checkEligibility() {
    if (this.captchaComponent && this.enableRecaptcha) {
      this.captchaComponent.execute();
    } else {
      this.getCaptchaToken();
    }
  }

  private setupEligibilityForm() {
    this.eligibilityForm = new UntypedFormGroup({
      cardNumber: new UntypedFormControl('', [Validators.required, ProductHomepageService.Validators.validCard()]),
    });
  }

  public ngOnDestroy() {
    this.reCaptchaService.setReCaptcha(false);
  }
}
